/* ==========================================================================
   XenVital — product page behaviour

   Small custom elements. Custom elements are used deliberately: the browser
   calls connectedCallback / disconnectedCallback for us, so every instance
   sets itself up and tears itself down when the Theme Editor re-renders a
   section. No duplicate listeners, no globals, and multiple instances of the
   same section on one page are safe.

   Nothing here reimplements Shopify behaviour. Variant switching, price
   rendering, add to cart and the cart drawer are all the theme's own code —
   xv-gallery simply exposes the setActiveMedia() method the theme's
   VariantSelects already calls, so variant-linked media works natively.
   ========================================================================== */

(() => {
  'use strict';

  class XVGallery extends HTMLElement {
    connectedCallback() {
      this.viewer = this.querySelector('[data-xv-viewer]');
      this.thumbs = Array.from(this.querySelectorAll('[data-xv-thumb]'));
      this.counter = this.querySelector('[data-xv-count]');
      if (!this.viewer) return;

      this.slides = Array.from(this.viewer.querySelectorAll('[data-media-id]'));

      this._onClick = (e) => {
        const thumb = e.target.closest('[data-xv-thumb]');
        if (thumb) { this.setActiveMedia(thumb.dataset.target, false); return; }
        if (e.target.closest('[data-xv-zoom]')) this._openLightbox();
      };
      this.addEventListener('click', this._onClick);

      this._onScroll = this._debounce(() => this._syncFromScroll(), 90);
      this.viewer.addEventListener('scroll', this._onScroll, { passive: true });

      this._updateCounter(0);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this._onClick);
      if (this.viewer && this._onScroll) this.viewer.removeEventListener('scroll', this._onScroll);
      if (this._dialog) this._dialog.remove();
    }

    /* Called by the theme's VariantSelects when a variant has featured media,
       with the same (mediaId, prepend) signature Dawn's media-gallery uses. */
    setActiveMedia(mediaId, prepend) {
      if (!this.viewer) return;
      const slide = this.viewer.querySelector('[data-media-id="' + mediaId + '"]');
      if (!slide) return;

      this.viewer.scrollTo({
        left: slide.offsetLeft - this.viewer.offsetLeft,
        behavior: prepend ? 'auto' : 'smooth'
      });

      this._setActive(mediaId);
      this._updateCounter(this.slides.indexOf(slide));

      if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();
    }

    _syncFromScroll() {
      if (!this.slides.length) return;
      const mid = this.viewer.scrollLeft + this.viewer.clientWidth / 2;
      let best = 0;
      let bestDist = Infinity;
      this.slides.forEach((slide, i) => {
        const centre = slide.offsetLeft - this.viewer.offsetLeft + slide.clientWidth / 2;
        const dist = Math.abs(centre - mid);
        if (dist < bestDist) { bestDist = dist; best = i; }
      });
      this._setActive(this.slides[best].dataset.mediaId);
      this._updateCounter(best);
    }

    _setActive(mediaId) {
      this.thumbs.forEach((t) => {
        const next = String(t.dataset.target === mediaId);
        if (t.getAttribute('aria-current') !== next) t.setAttribute('aria-current', next);
      });
      this.slides.forEach((s) => s.classList.toggle('is-active', s.dataset.mediaId === mediaId));
    }

    _updateCounter(index) {
      if (!this.counter || !this.slides.length) return;
      const next = (index + 1) + ' / ' + this.slides.length;
      if (this.counter.textContent !== next) this.counter.textContent = next;
    }

    _openLightbox() {
      const active = this.slides.find((s) => s.classList.contains('is-active')) || this.slides[0];
      const img = active && active.querySelector('img');
      if (!img) return;

      if (!this._dialog) {
        const d = document.createElement('dialog');
        d.className = 'xv-lightbox';
        d.innerHTML = '<button class="xv-lightbox__close" type="button" aria-label="Close">&times;</button><img alt="">';
        d.addEventListener('click', (e) => {
          if (e.target === d || e.target.closest('.xv-lightbox__close')) d.close();
        });
        this.appendChild(d);
        this._dialog = d;
      }

      const target = this._dialog.querySelector('img');
      target.src = img.currentSrc || img.src;
      target.alt = img.alt || '';
      if (typeof this._dialog.showModal === 'function') this._dialog.showModal();
    }

    _debounce(fn, wait) {
      let t;
      return function () { clearTimeout(t); t = setTimeout(fn, wait); };
    }
  }

  /* Quantity stepper around the theme's .quantity__input, so product-info.js
     keeps managing min / max / step rules. */
  class XVQty extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      if (!this.input) return;
      this._onClick = (e) => {
        const btn = e.target.closest('button[data-step]');
        if (!btn) return;
        e.preventDefault();
        if (Number(btn.dataset.step) > 0) this.input.stepUp();
        else this.input.stepDown();
        this.input.dispatchEvent(new Event('change', { bubbles: true }));
      };
      this.addEventListener('click', this._onClick);
    }
    disconnectedCallback() { this.removeEventListener('click', this._onClick); }
  }

  /* Mobile purchase bar. Mirrors the live price and button state, and submits
     the real product form so the theme's cart pipeline handles everything. */
  class XVStickyBar extends HTMLElement {
    connectedCallback() {
      const id = this.dataset.section;
      this.form = document.getElementById('product-form-' + id);
      this.anchor = document.getElementById('xv-cta-' + id);
      this.priceSource = document.getElementById('price-' + id);
      this.priceTarget = this.querySelector('[data-xv-sticky-price]');
      this.button = this.querySelector('[data-xv-sticky-add]');
      this.ctaPrice = this.anchor ? this.anchor.querySelector('[data-xv-cta-price]') : null;
      if (!this.form || !this.anchor) return;

      this._observer = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          this.dataset.visible = String(!entry.isIntersecting && entry.boundingClientRect.top < 0);
        },
        { rootMargin: '0px 0px -100% 0px', threshold: 0 }
      );
      this._observer.observe(this.anchor);

      this._onClick = (e) => {
        e.preventDefault();
        if (this.button.getAttribute('aria-disabled') === 'true') return;
        if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
        else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      };
      if (this.button) this.button.addEventListener('click', this._onClick);

      this._mirror = () => this._sync();
      if (this.priceSource) {
        this._mo = new MutationObserver(this._mirror);
        this._mo.observe(this.priceSource, { childList: true, subtree: true, characterData: true });
      }
      const mainBtn = this.form.querySelector('[name="add"]');
      if (mainBtn) {
        this._btnMo = new MutationObserver(this._mirror);
        this._btnMo.observe(mainBtn, {
          childList: true, subtree: true, characterData: true,
          attributes: true, attributeFilter: ['disabled', 'aria-disabled']
        });
      }
      this._sync();
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._mo) this._mo.disconnect();
      if (this._btnMo) this._btnMo.disconnect();
      if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
    }

    /* Every write below is guarded by an equality check. That matters: the CTA
       price node sits INSIDE the Add to cart button, which is the same node
       _btnMo observes, so an unguarded write would retrigger the observer and
       spin forever. Writing only on a real change lets it settle after one pass. */
    _sync() {
      if (this.priceTarget && this.priceSource) {
        const html = this.priceSource.innerHTML;
        if (this.priceTarget.innerHTML !== html) this.priceTarget.innerHTML = html;
      }
      if (this.ctaPrice && this.priceSource) {
        const current = this.priceSource.querySelector('[data-xv-current-price]');
        if (current) {
          const next = '• ' + current.textContent.trim();
          if (this.ctaPrice.textContent.trim() !== next) this.ctaPrice.textContent = next;
        }
      }
      const mainBtn = this.form.querySelector('[name="add"]');
      if (!mainBtn || !this.button) return;
      const label = mainBtn.querySelector('span');
      const labelTarget = this.button.querySelector('span');
      if (label && labelTarget) {
        const text = label.textContent.trim();
        if (labelTarget.textContent.trim() !== text) labelTarget.textContent = text;
      }
      const disabled = String(mainBtn.hasAttribute('disabled') || mainBtn.getAttribute('aria-disabled') === 'true');
      if (this.button.getAttribute('aria-disabled') !== disabled) {
        this.button.setAttribute('aria-disabled', disabled);
      }
    }
  }

  /* Shopify's own Product Recommendations API, rendered by the section itself
     through the Section Rendering API. */
  class XVRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;
      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const fresh = doc.querySelector('.xv-grid');
          if (!fresh || !fresh.children.length) return;
          const target = this.querySelector('[data-xv-rec-grid]');
          if (target) target.innerHTML = fresh.innerHTML;
        })
        .catch(() => {});
    }
  }

  /* Remembers product ids locally and asks Shopify to render the cards. No
     product data is stored in the browser, only ids. */
  class XVRecentlyViewed extends HTMLElement {
    connectedCallback() {
      const KEY = 'xv:recently-viewed';
      const current = this.dataset.productId;
      const limit = Number(this.dataset.limit || 4);

      let ids = [];
      try { ids = JSON.parse(localStorage.getItem(KEY) || '[]'); } catch (e) { ids = []; }
      if (!Array.isArray(ids)) ids = [];

      const others = ids.filter((id) => String(id) !== String(current)).slice(0, limit);

      if (current) {
        const next = [current].concat(ids.filter((id) => String(id) !== String(current))).slice(0, 12);
        try { localStorage.setItem(KEY, JSON.stringify(next)); } catch (e) {}
      }

      if (!others.length) return;

      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const query = others.map((id) => 'id:' + id).join(' OR ');
      const url = root + 'search?type=product&q=' + encodeURIComponent(query) + '&section_id=' + this.dataset.sectionId;

      fetch(url)
        .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
        .then((text) => {
          const doc = new DOMParser().parseFromString(text, 'text/html');
          const grid = doc.querySelector('[data-xv-recent-grid]');
          const target = this.querySelector('[data-xv-recent-grid]');
          if (!grid || !target || !grid.children.length) return;
          target.innerHTML = grid.innerHTML;
          this.hidden = false;
        })
        .catch(() => {});
    }
  }

  if (!customElements.get('xv-gallery')) customElements.define('xv-gallery', XVGallery);
  if (!customElements.get('xv-qty')) customElements.define('xv-qty', XVQty);
  if (!customElements.get('xv-sticky-bar')) customElements.define('xv-sticky-bar', XVStickyBar);
  if (!customElements.get('xv-recommendations')) customElements.define('xv-recommendations', XVRecommendations);
  if (!customElements.get('xv-recently-viewed')) customElements.define('xv-recently-viewed', XVRecentlyViewed);
})();